"use client";

import { useState } from "react";
import { Chrome } from "@/components/ui/tls/chrome";
import { Mono } from "@/components/ui/tls/mono";
import { announce } from "@/lib/announce";

export interface GuideModel {
  id: string;
  name: string;
  years: string;
  usbPath: string;
  /** Optional per-model quick-answer override; falls back to the shared default. */
  quickAnswer?: string[];
}

interface GuideModelPickerProps {
  models: GuideModel[];
  /** Default selected model ID. */
  defaultModelId?: string;
  /** Default quick-answer steps shown when a model has no override. */
  defaultQuickAnswer: string[];
  /** Updated-on line under the picker. */
  updatedNote: string;
  /**
   * ARIA label for the model button group.
   * Defaults to English "Pick your Tesla model".
   */
  pickerGroupLabel?: string;
  /**
   * Template for individual model button aria-label.
   * Use {model} as placeholder for the model name.
   * Defaults to English "Show install path for {model}".
   */
  pickerButtonLabel?: string;
  /**
   * Label for the aria-live region announcing model changes.
   * Defaults to English "Selected model".
   */
  pickerLiveRegionLabel?: string;
  /**
   * Template for the quick-answer box Chrome eyebrow.
   * Use {model} as placeholder for the model name (lowercased).
   * Defaults to English "{model} quick answer".
   */
  pickerQuickAnswerLabel?: string;
  /**
   * Eyebrow label for the picker card ("your tesla").
   */
  pickerEyebrow?: string;
}

/**
 * Interactive Tesla model picker for the install guide.
 *
 * Lifts the active state out of the server page so visitors can switch models
 * and immediately see the right USB path + quick-answer flow without a
 * full-page reload. Fully keyboard-accessible — `<ModelBadge>` renders a
 * native button.
 */
export default function GuideModelPicker({
  models,
  defaultModelId = "Y",
  defaultQuickAnswer,
  updatedNote,
  pickerGroupLabel = "Pick your Tesla model",
  pickerButtonLabel = "Show install path for {model}",
  pickerLiveRegionLabel = "Selected model",
  pickerQuickAnswerLabel = "{model} quick answer",
  pickerEyebrow = "your tesla",
}: GuideModelPickerProps) {
  // Selection is local to this picker. It used to seed from, and write back
  // to, a site-wide saved vehicle in localStorage; that store went away with
  // the vehicle picker (2026-08-15) because it drove a per-sound "verified for
  // your model" badge that every sound satisfied identically. The USB path
  // this picker exists to show is still per-model, which is real work.
  const [activeId, setActiveId] = useState(defaultModelId);

  const active = models.find(m => m.id === activeId) ?? models[0];
  const quickAnswer = active.quickAnswer ?? defaultQuickAnswer;

  const handleSelect = (id: string) => {
    setActiveId(id);
    // §11.1: the model info panel below is visible content, not a broadcast
    // node — lib/announce.ts owns the actual aria-live write so this join
    // doesn't fight the shell's single live region.
    const selected = models.find(m => m.id === id);
    if (selected) {
      announce(
        `${pickerLiveRegionLabel}: ${selected.name}, ${selected.years}. USB path ${selected.usbPath}.`,
        "polite"
      );
    }
  };

  return (
    <div
      className="py-6"
      style={{
        // De-carded: the picker is the guide's primary content, not a widget
        // dropped onto it. It was the heaviest box on the page — a filled
        // surface inside a STRONG border inside a large radius — and it now
        // sits on the canvas between two rules.
        borderTop: "1px solid var(--tls-border-subtle)",
        borderBottom: "1px solid var(--tls-border-subtle)",
      }}
    >
      <Chrome color="var(--tls-fg-subtle)" style={{ display: "block", marginBottom: 12 }}>
        {pickerEyebrow}
      </Chrome>

      {/* Model pill row */}
      {/* eslint-disable-next-line react-doctor/prefer-tag-over-role -- role=group on a toolbar div; no semantic HTML element maps to a button-group toolbar */}
      <div className="flex flex-wrap gap-2 mb-5" role="group" aria-label={pickerGroupLabel}>
        {models.map(m => (
          <button
            key={m.id}
            type="button"
            onClick={() => handleSelect(m.id)}
            aria-pressed={m.id === activeId}
            /* Label-in-Name (WCAG 2.5.3): the visible pill text is `m.id`
               ("S", "CT", ...), so the accessible name must contain it.
               Prepend the id before the localized "{model}" description. */
            aria-label={`${m.id}: ${pickerButtonLabel.replace("{model}", m.name)}`}
            // min-w-16: a one-glyph label ("S", "3") inside a 44px pill
            // otherwise squares off and reads as a circle, which the DS
            // reserves for play controls (A-8). Wider than tall = pill.
            // minWidth inline, not `min-w-16`: `.tlsr-pill` sets min-width:44px
            // UNLAYERED, which outranks any Tailwind utility, so the class
            // alone left these painting as 44px circles.
            // 56, not 64: wider than the 44 height (so the 999 radius reads as
            // a stadium, not a circle) and still narrow enough that all five
            // models fit one row in the guide's right column.
            style={{ minWidth: 56 }}
            className={`tlsr-pill tlsr-focus${m.id === activeId ? " is-active" : ""}`}
          >
            {m.id}
          </button>
        ))}
      </div>

      {/* Selected model info */}
      <div
        className="py-4"
        style={{
          // The picker itself is de-carded, so a boxed panel inside it is a
          // container nested in a container again. The selected model is the
          // answer to the control above it; a rule ties them together.
          borderTop: "1px solid var(--tls-border-subtle)",
        }}
      >
        <div className="font-display text-lg font-semibold text-[var(--tls-fg-strong)]">
          {active.name}
        </div>
        <Mono size={11} color="var(--tls-fg-muted)" style={{ display: "block", marginTop: 4 }}>
          {active.years}
        </Mono>
        {/* The USB path is meta, not one of the accent's six roles, so it is ink. */}
        <div className="mt-3" style={{ color: "var(--tls-fg-muted)" }}>
          <Mono size={11}>{active.usbPath}</Mono>
        </div>
      </div>

      {/* Quick-answer box */}
      <div
        className="mt-4 py-4"
        style={{
          borderTop: "1px solid var(--tls-border-subtle)",
        }}
      >
        <Chrome color="var(--tls-fg-subtle)" style={{ display: "block", marginBottom: 8 }}>
          {pickerQuickAnswerLabel.replace("{model}", active.name.toLowerCase())}
        </Chrome>
        <ol className="space-y-1.5 text-sm text-[var(--tls-fg-muted)] list-decimal list-inside">
          {quickAnswer.map((step, i) => (
            // eslint-disable-next-line react-doctor/no-danger
            <li key={i} dangerouslySetInnerHTML={{ __html: step }} />
          ))}
        </ol>
      </div>

      <p className="mt-3 text-sm text-[var(--tls-fg-subtle)]">{updatedNote}</p>
    </div>
  );
}
